import type { CSSProperties } from 'react';
import { getFramework } from '@/framework';
import { getLocaleStore } from '@/i18n';
import { type GroupLayer } from '@/layers/group-layer';
import { type IMenuItemProps, type IRenderContextMenuItemArgs } from '@/plugins/context-menu';
import { isHotKeyPress } from '@/utils/event-kit';
import { LayerType, type ILayerProps } from '@/version-types';

export const layerOverlapItem = ({ activeLayer, pickLayer }: IRenderContextMenuItemArgs, identity: string) => {
    const { $t } = getLocaleStore();
    const framework = getFramework(identity);
    if (framework.service.edgelessCanvas.isEdgelessMode) return null;

    const layer = pickLayer || activeLayer;
    const disabled = !layer;

    if (disabled || !activeLayer) return null;

    const item: IMenuItemProps = {
        name: $t((v) => v.contextMenuLayer.menuConfig.overlap),
        type: 'overlap',
        childMenus: [],
    };

    const tryGetGroupLayer = () => {
        const groupLayer = framework.service.registration.getInstance<GroupLayer>('GroupLayer');
        if (!groupLayer) {
            throw new Error('GroupLayer is not found');
        }
        return groupLayer;
    };

    const generateGroupAndActive = (layer: ILayerProps, currentLayer: ILayerProps) => {
        if (currentLayer.lock || currentLayer.hidden) return;
        const parentLayer = framework.service.flatLayer.getWrapLayer(currentLayer.id, true);
        if (!parentLayer) return;
        if (layer.id === parentLayer.id || layer.id === currentLayer.id) {
            framework.service.frame.setActiveLayer(currentLayer.id);
            return;
        }
        let pickIds: string[] = [];
        if (framework.service.flatLayer.checkIsExtraGroup(layer)) {
            tryGetGroupLayer().unpack(layer.id);
            pickIds = layer.children.map((child) => child.id);
        } else {
            pickIds = [layer.id];
        }
        tryGetGroupLayer().createGroupAndActive([...pickIds, parentLayer.id]);
    };

    const layerOverlapData = framework.service.frame.getLayerOverLapList(activeLayer.id);

    const genMenuListLayer = (layers: ILayerProps[], isRoot: boolean, style: CSSProperties = {}) => {
        layers.forEach((subLayer) => {
            const { id, layerType, children } = subLayer;
            const behavior = framework.service.behavior.get(subLayer);
            if (!behavior) return;
            const name = behavior.getMenuOverlapName(subLayer);
            const layerLevel: IMenuItemProps = {
                style,
                name,
                type: subLayer.id,
                active: activeLayer.id === subLayer.id,
                icon: <div className="l-layer__icon">{behavior.getMenuOverlapIcon(subLayer)}</div>,
                onClick: () => {
                    const hotKeyDown = isHotKeyPress();
                    if (hotKeyDown) {
                        if (activeLayer) {
                            generateGroupAndActive(activeLayer, subLayer);
                        }
                    } else {
                        if (subLayer.hidden) return;
                        framework.service.frame.setActiveLayer(id);
                    }
                },
                onMouseEnter: () => {
                    framework.service.frame.setSelected(id);
                },
                onMouseLeave: () => {
                    framework.service.frame.clearSelected();
                },
            };
            if (layerType === LayerType.GROUP_LAYER) {
                if (isRoot) {
                    item.childMenus!.push(layerLevel);
                }
                genMenuListLayer(children, false, { paddingLeft: 38 });
            } else {
                item.childMenus!.push(layerLevel);
            }
        });
    };
    genMenuListLayer(layerOverlapData, true);

    return item;
};
